/**
 * Types partagés pour le Community Intelligence Dashboard.
 * Reflètent 1:1 les colonnes retournées par les RPC `ci_*`.
 */

export interface AuthorizedPool {
  league_id: number;
  league_name: string;
  league_type: string;
  joined_at: string | null;
}

export interface DashboardOverview {
  league_id: number;
  generated_at: string;
  /** Début de saison CI (timestamptz) : aucun compteur d'activité ne remonte avant. */
  season_start?: string;
  total_poolers: number;
  completed_builds: number;
  incomplete_builds: number;
  new_joiners_last_7d: number;
  new_joiners_last_30d: number;
  new_joiners_last_90d: number;
  trades_all_time: number;
  trades_last_7d: number;
  trades_last_30d: number;
  trades_last_90d: number;
  signings_all_time: number;
  signings_last_7d: number;
  signings_last_30d: number;
  signings_last_90d: number;
  drops_all_time: number;
  drops_last_7d: number;
  drops_last_30d: number;
  drops_last_90d: number;
  // Mouvements LTIR (ltirhistory, suivis depuis le 2026-10-13). Optionnels
  // tant que l'ancienne version de la RPC peut encore répondre.
  ltir_placed_all_time?: number;
  ltir_placed_last_7d?: number;
  ltir_placed_last_30d?: number;
  ltir_placed_last_90d?: number;
  ltir_reactivated_all_time?: number;
  ltir_reactivated_last_7d?: number;
  ltir_reactivated_last_30d?: number;
  ltir_reactivated_last_90d?: number;
  roster_slots_filled: number;
}

export interface ActivityDayPoint {
  day: string; // YYYY-MM-DD
  trades: number;
  signings: number;
  /** Libérations (freeagentsreleasehistory). */
  drops: number;
  ltir_placed: number;
  ltir_reactivated: number;
  new_joiners: number;
}

export interface EngagementComparison {
  league_id: number;
  days: number;
  generated_at: string;
  season_start?: string;
  /** false quand la période précédente commence avant la saison : ne pas comparer. */
  previous_complete?: boolean;
  signings_current: number;
  signings_previous: number;
  poolers_signing_current: number;
  poolers_signing_previous: number;
  new_poolers_current: number;
  new_poolers_previous: number;
  // Compteurs informatifs hors score d'engagement (suivis depuis le 2026-10-13).
  releases_current?: number;
  releases_previous?: number;
  ltir_placed_current?: number;
  ltir_placed_previous?: number;
  ltir_reactivated_current?: number;
  ltir_reactivated_previous?: number;
}

export interface SigningPlayerComparison extends TopPlayer {
  current_count: number;
  previous_count: number;
  latest_event_at: string | null;
}

export interface RosterInjuryNews {
  event_type: 'injury' | 'return';
  player_id: number;
  player_name: string | null;
  event_time: string;
  status: string | null;
  description: string | null;
}

/** Détention actuelle d'un joueur dans les rosters du pool (ci_get_player_ownership). */
export interface PlayerOwnership {
  player_id: number;
  /** Équipes distinctes qui ont le joueur dans leur roster. */
  owners: number;
  total_poolers: number;
  /** Équipes qui l'alignent : section LNH, ni réserviste ni LTIR. */
  active_owners: number;
  /** Équipes qui l'ont placé en LTIR. */
  ltir_owners: number;
}

export type TopPlayerMetric = 'signed' | 'dropped' | 'traded' | 'injured';

export interface TopPlayer {
  player_id: number;
  player_name: string | null;
  event_count: number;
}

export type ActivityEventType =
  | 'trade'
  | 'signing'
  | 'drop'
  | 'ltir_placed'
  | 'ltir_reactivated';

export interface RecentActivityEvent {
  event_type: ActivityEventType;
  event_time: string;
  user_id: number | null;
  username: string | null;
  player_id: number | null;
  player_name: string | null;
  details: Record<string, unknown>;
}

export interface ActivitySearchOption {
  id: number;
  label: string;
}

export type TimeWindow = '7d' | '30d' | '90d' | 'season';

export const TIME_WINDOW_DAYS: Record<TimeWindow, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  season: null, // NULL = toute la saison côté RPC (depuis ci_season_start)
};

export const TIME_WINDOW_LABELS: Record<TimeWindow, string> = {
  '7d': '7 jours',
  '30d': '30 jours',
  '90d': '90 jours',
  season: 'Saison',
};

/**
 * Libérations et mouvements LTIR : les tables `freeagentsreleasehistory` et
 * `ltirhistory` n'existent que depuis cette date ; rien n'est compté avant.
 */
export const ROSTER_MOVES_TRACKED_SINCE = '13 octobre 2026';

/** Pooler dont l'équipe est incomplète (`ci_get_incomplete_poolers`). */
export interface IncompletePooler {
  user_id: number;
  username: string | null;
  email: string | null;
  joined_at: string | null;
}
